import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

export type UserType = "BACK_OFFICE" | "CLUB_USER";

/** Shape of GET /api/me — the authenticated user plus resolved authorization context. */
export interface Me {
  id: string;
  email: string;
  name: string;
  userType: UserType;
  clubId: string | null;
  language: string;
  /** Profile fields surfaced for the Settings → Profile section (TW-3463/3464). */
  image: string | null;
  jobTitle: string | null;
  phone: string | null;
  timezone: string | null;
  /** The user's club name (null for back-office) — read-only on the Profile section (TW-3464). */
  clubName: string | null;
  /** Interface density preference — drives the `.dense` class app-wide (TW-3466). */
  density: "regular" | "compact";
  roles: string[];
  /** Effective atomic permission keys — gate UI actions on these, never on role names. */
  permissions: string[];
  /** Optional capabilities toggled by env — hide gated UI when off (TW-3447). */
  features?: { passengerDocUpload?: boolean };
}

interface AuthzValue {
  me: Me | null;
  isLoading: boolean;
  /** True once the user holds the given atomic permission. */
  has: (permission: string) => boolean;
  hasAll: (...permissions: string[]) => boolean;
  hasAny: (...permissions: string[]) => boolean;
  userType: UserType | null;
  isBackOffice: boolean;
  /** Re-fetch GET /api/me — e.g. after a profile/photo edit, so the topbar + user card update. */
  refresh: () => Promise<void>;
}

const AuthzContext = createContext<AuthzValue | null>(null);

/**
 * Loads the session's authorization context (GET /api/me) and exposes permission helpers
 * (architecture §5.4, "session-aware role/permission helpers"). Mount inside the
 * authenticated tree — it fetches once on mount, which only happens after RequireAuth has a
 * session. Mirrors the backend rule: code checks atomic permissions, never role names.
 */
export function AuthzProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/me`, { headers: { accept: "application/json" } });
      setMe(res.ok ? ((await res.json()) as Me) : null);
    } catch {
      setMe(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthzValue>(() => {
    const granted = new Set(me?.permissions ?? []);
    return {
      me,
      isLoading,
      has: (permission) => granted.has(permission),
      hasAll: (...permissions) => permissions.every((p) => granted.has(p)),
      hasAny: (...permissions) => permissions.some((p) => granted.has(p)),
      userType: me?.userType ?? null,
      isBackOffice: me?.userType === "BACK_OFFICE",
      refresh,
    };
  }, [me, isLoading, refresh]);

  return <AuthzContext.Provider value={value}>{children}</AuthzContext.Provider>;
}

/** Access the authorization context. Throws if used outside <AuthzProvider>. */
export function useAuthz(): AuthzValue {
  const ctx = useContext(AuthzContext);
  if (!ctx) throw new Error("useAuthz must be used within <AuthzProvider>");
  return ctx;
}

/** Convenience: true if the current user holds `permission`. */
export function useCan(permission: string): boolean {
  return useAuthz().has(permission);
}

interface CanProps {
  /** Require this single permission. */
  permission?: string;
  /** Require ANY of these permissions. */
  anyOf?: string[];
  /** Require ALL of these permissions. */
  allOf?: string[];
  /** Require the user to be of this type/surface. */
  userType?: UserType;
  /** Rendered when the check fails (default: nothing). */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Renders `children` only when the current user satisfies the given check; otherwise renders
 * `fallback`. Use to hide actions a user lacks:
 *
 *   <Can permission="invoice.issue"><Button>Issue invoice</Button></Can>
 *   <Can userType="BACK_OFFICE">…</Can>
 */
export function Can({ permission, anyOf, allOf, userType, fallback = null, children }: CanProps) {
  const authz = useAuthz();
  const ok =
    (permission === undefined || authz.has(permission)) &&
    (anyOf === undefined || authz.hasAny(...anyOf)) &&
    (allOf === undefined || authz.hasAll(...allOf)) &&
    (userType === undefined || authz.userType === userType);
  return <>{ok ? children : fallback}</>;
}
